// src/pages/ProspectingPipeline.jsx
// Agent Prospecting Pipeline — Kanban, table, campaigns, lead sources, analytics
import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Target, Plus, Search, Filter, RefreshCw, AlertTriangle,
  Shield, CheckCircle2, Clock, ArrowRight, Phone, Mail,
  BarChart3, TrendingUp, Users, Calendar, X, Check,
  ChevronDown, ChevronRight, MessageSquare, Zap, Star,
  ExternalLink, Copy, Info, AlertCircle, Activity,
  FileText, DollarSign, Globe, Upload
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  ResponsiveContainer, AreaChart, Area
} from 'recharts';

const BASE = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const STAGES = [
  { key: 'lead',            label: 'Lead',        color: 'text-slate-400',   border: 'border-slate-500',   bg: 'bg-slate-500',   sla: 7    },
  { key: 'qualified',       label: 'Qualified',   color: 'text-blue-400',    border: 'border-blue-500',    bg: 'bg-blue-500',    sla: 14   },
  { key: 'appointment_set', label: 'Appt Set',    color: 'text-purple-400',  border: 'border-purple-500',  bg: 'bg-purple-500',  sla: 7    },
  { key: 'follow_up',       label: 'Follow Up',   color: 'text-amber-400',   border: 'border-amber-500',   bg: 'bg-amber-500',   sla: 21   },
  { key: 'closed_won',      label: 'Closed Won',  color: 'text-emerald-400', border: 'border-emerald-500', bg: 'bg-emerald-500', sla: 9999 },
  { key: 'closed_lost',     label: 'Closed Lost', color: 'text-red-400',     border: 'border-red-500',     bg: 'bg-red-500',     sla: 9999 },
];

const INSURANCE_TYPES = {
  medicare_advantage: { label: 'MA',     color: 'bg-blue-900/40 text-blue-400 border-blue-500/30'     },
  medsup:             { label: 'MedSup', color: 'bg-purple-900/40 text-purple-400 border-purple-500/30' },
  pdp:                { label: 'PDP',    color: 'bg-amber-900/40 text-amber-400 border-amber-500/30'   },
  life:               { label: 'Life',   color: 'bg-green-900/40 text-green-400 border-green-500/30'   },
  other:              { label: 'Other',  color: 'bg-gray-800 text-gray-400 border-gray-600'            },
};

const LEAD_SOURCES = [
  'referral', 'social_media', 'facebook_ad', 'google_ad',
  'direct_mail', 'web_form', 'purchased_list', 't65_aged', 'cold_call', 'other'
];

const PRIORITY_COLORS = {
  urgent: 'bg-red-500',
  high:   'bg-amber-500',
  normal: 'bg-gray-500',
  low:    'bg-gray-700',
};

// ---------------------------------------------------------------------------
// Utilities
// ---------------------------------------------------------------------------
function timeAgo(ts) {
  if (!ts) return null;
  const diff = Date.now() - new Date(ts).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 2)  return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24)  return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days}d ago`;
  return `${Math.floor(days / 30)}mo ago`;
}

function daysUntil(dateStr) {
  if (!dateStr) return null;
  const target = new Date(dateStr);
  const now = new Date();
  return Math.ceil((target - now) / 86400000);
}

function daysInStageColor(days) {
  if (days <= 7)  return 'text-green-400';
  if (days <= 14) return 'text-amber-400';
  return 'text-red-400';
}

function sourceLabel(src) {
  if (!src) return 'Unknown';
  return src.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

function fmtDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function fmtPhone(phone) {
  if (!phone) return '—';
  const d = phone.replace(/\D/g, '');
  if (d.length === 10) return `(${d.slice(0,3)}) ${d.slice(3,6)}-${d.slice(6)}`;
  if (d.length === 11)  return `(${d.slice(1,4)}) ${d.slice(4,7)}-${d.slice(7)}`;
  return phone;
}

// ---------------------------------------------------------------------------
// ProspectCard
// ---------------------------------------------------------------------------
function ProspectCard({ prospect, onClick, onDragStart }) {
  const stage    = STAGES.find(s => s.key === prospect.stage) || STAGES[0];
  const insType  = INSURANCE_TYPES[prospect.insurance_type] || INSURANCE_TYPES.other;
  const daysIn   = prospect.days_in_stage ?? 0;
  const t65Days  = daysUntil(prospect.t65_date);
  const priority = PRIORITY_COLORS[prospect.priority] || PRIORITY_COLORS.normal;

  return (
    <div
      draggable={true}
      onDragStart={onDragStart}
      onClick={onClick}
      className="bg-gray-800 border border-gray-700 rounded-lg p-3 cursor-pointer hover:border-gray-500 hover:bg-gray-750 transition group"
    >
      {/* Name row */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <span className={`w-2 h-2 rounded-full flex-shrink-0 ${priority}`} title={`Priority: ${prospect.priority || 'normal'}`} />
          <span className="text-white text-sm font-semibold truncate">
            {prospect.first_name} {prospect.last_name}
          </span>
        </div>
        {/* TCPA */}
        {prospect.tcpa_consent === true
          ? <Shield size={13} className="text-green-400 flex-shrink-0" title="TCPA Consent Obtained" />
          : <AlertTriangle size={13} className="text-red-400 flex-shrink-0" title="No TCPA Consent" />
        }
      </div>

      {/* Badges row */}
      <div className="flex flex-wrap gap-1 mb-2">
        <span className={`px-1.5 py-0.5 rounded text-xs border ${insType.color}`}>
          {insType.label}
        </span>
        {/* T65 countdown */}
        {t65Days !== null && t65Days >= 0 && t65Days <= 180 && (
          <span className={`px-1.5 py-0.5 rounded text-xs border ${
            t65Days <= 30
              ? 'bg-red-900/40 text-red-400 border-red-500/30'
              : 'bg-amber-900/40 text-amber-400 border-amber-500/30'
          }`}>
            {t65Days}d to T65
          </span>
        )}
      </div>

      {/* Days in stage */}
      <div className="flex items-center justify-between text-xs">
        <span className={`${daysInStageColor(daysIn)}`}>{daysIn}d in stage</span>
        {prospect.last_activity_at && (
          <span className="text-gray-500">{timeAgo(prospect.last_activity_at)}</span>
        )}
      </div>

      {/* Last activity snippet */}
      {prospect.last_activity && (
        <p className="text-gray-500 text-xs mt-1 truncate">{prospect.last_activity}</p>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// PipelineTab
// ---------------------------------------------------------------------------
function PipelineTab({ pipeline, loading, onProspectClick, onAddProspect, moveStage, setShowConvertModal }) {
  const [dragOverStage, setDragOverStage] = useState(null);

  const allProspects = Object.values(pipeline).flat();

  return (
    <div>
      {loading && (
        <div className="flex items-center gap-2 text-gray-400 text-sm mb-4">
          <RefreshCw size={14} className="animate-spin" /> Loading pipeline...
        </div>
      )}

      <div className="grid gap-3" style={{ gridTemplateColumns: `repeat(${STAGES.length}, minmax(180px, 1fr))` }}>
        {STAGES.map(stage => {
          const cards = pipeline[stage.key] || [];
          const avgDays = cards.length
            ? Math.round(cards.reduce((a, c) => a + (c.days_in_stage || 0), 0) / cards.length)
            : 0;

          return (
            <div
              key={stage.key}
              className={`flex flex-col rounded-xl border transition ${
                dragOverStage === stage.key
                  ? `${stage.border} bg-gray-800/60`
                  : 'border-gray-700 bg-gray-900'
              }`}
              onDragOver={(e) => { e.preventDefault(); setDragOverStage(stage.key); }}
              onDragLeave={() => setDragOverStage(null)}
              onDrop={(e) => {
                e.preventDefault();
                const prospectId = e.dataTransfer.getData('prospectId');
                setDragOverStage(null);
                if (!prospectId) return;
                if (stage.key === 'closed_won') {
                  const prospect = allProspects.find(p => String(p.id) === String(prospectId));
                  if (prospect) setShowConvertModal(prospect);
                } else {
                  moveStage(prospectId, stage.key);
                }
              }}
            >
              {/* Column header */}
              <div className="px-3 pt-3 pb-2">
                <div className="flex items-center justify-between mb-0.5">
                  <span className={`text-sm font-semibold ${stage.color}`}>{stage.label}</span>
                  <span className="text-xs bg-gray-800 text-gray-400 rounded-full px-2 py-0.5">
                    {cards.length}
                  </span>
                </div>
                <p className="text-gray-600 text-xs">avg {avgDays}d in stage</p>
              </div>

              {/* Add button */}
              <div className="px-3 pb-2">
                <button
                  onClick={() => onAddProspect(stage.key)}
                  className="w-full text-xs text-gray-500 hover:text-white border border-dashed border-gray-700 hover:border-gray-500 rounded-lg py-1.5 transition flex items-center justify-center gap-1"
                >
                  <Plus size={12} /> Add
                </button>
              </div>

              {/* Cards */}
              <div className="flex-1 px-3 pb-3 flex flex-col gap-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
                {cards.map(p => (
                  <ProspectCard
                    key={p.id}
                    prospect={p}
                    onClick={() => onProspectClick(p)}
                    onDragStart={(e) => e.dataTransfer.setData('prospectId', String(p.id))}
                  />
                ))}
                {cards.length === 0 && (
                  <div className="text-center text-gray-700 text-xs py-4">Empty</div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProspectsTab (table)
// ---------------------------------------------------------------------------
function ProspectsTab({ prospects, onProspectClick, onAddProspect, onReload }) {
  const [search, setSearch]       = useState('');
  const [stageFilter, setStageFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [sortCol, setSortCol]     = useState('created_at');
  const [sortDir, setSortDir]     = useState('desc');
  const [selected, setSelected]   = useState([]);

  const filtered = prospects.filter(p => {
    if (stageFilter && p.stage !== stageFilter) return false;
    if (sourceFilter && p.lead_source !== sourceFilter) return false;
    if (search) {
      const q = search.toLowerCase();
      return (
        (p.first_name || '').toLowerCase().includes(q) ||
        (p.last_name  || '').toLowerCase().includes(q) ||
        (p.email      || '').toLowerCase().includes(q) ||
        (p.phone      || '').toLowerCase().includes(q)
      );
    }
    return true;
  }).sort((a, b) => {
    const va = a[sortCol] ?? '';
    const vb = b[sortCol] ?? '';
    if (va < vb) return sortDir === 'asc' ? -1 : 1;
    if (va > vb) return sortDir === 'asc' ?  1 : -1;
    return 0;
  });

  function toggleSort(col) {
    if (sortCol === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortCol(col); setSortDir('asc'); }
  }

  function toggleSelect(id) {
    setSelected(s => s.includes(id) ? s.filter(x => x !== id) : [...s, id]);
  }

  function toggleAll() {
    setSelected(selected.length === filtered.length ? [] : filtered.map(p => p.id));
  }

  async function exportSelected() {
    const rows = filtered.filter(p => selected.includes(p.id));
    const header = ['First Name','Last Name','Phone','Email','Stage','Lead Source','Insurance Type','T65 Date','TCPA','Days in Stage','Added'];
    const csv = [header, ...rows.map(p => [
      p.first_name, p.last_name, p.phone, p.email,
      p.stage, p.lead_source, p.insurance_type,
      fmtDate(p.t65_date), p.tcpa_consent ? 'Yes' : 'No',
      p.days_in_stage, fmtDate(p.created_at)
    ])].map(r => r.map(v => `"${(v ?? '').toString().replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'prospects.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  const SortIcon = ({ col }) => (
    <span className={`ml-1 ${sortCol === col ? 'text-amber-400' : 'text-gray-600'}`}>
      {sortCol === col ? (sortDir === 'asc' ? '▲' : '▼') : '⇅'}
    </span>
  );

  return (
    <div>
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative flex-1 min-w-48">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search prospects..."
            className="w-full bg-gray-900 border border-gray-700 rounded-lg pl-9 pr-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-400"
          />
        </div>
        <select
          value={stageFilter}
          onChange={e => setStageFilter(e.target.value)}
          className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-400"
        >
          <option value="">All Stages</option>
          {STAGES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
        </select>
        <select
          value={sourceFilter}
          onChange={e => setSourceFilter(e.target.value)}
          className="bg-gray-900 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-400"
        >
          <option value="">All Sources</option>
          {LEAD_SOURCES.map(s => <option key={s} value={s}>{sourceLabel(s)}</option>)}
        </select>
        <button onClick={onReload} className="p-2 text-gray-400 hover:text-white hover:bg-gray-800 rounded-lg transition">
          <RefreshCw size={16} />
        </button>
        <button
          onClick={() => onAddProspect('lead')}
          className="bg-amber-400 text-gray-900 font-semibold px-4 py-2 rounded-lg hover:bg-amber-300 transition text-sm flex items-center gap-2"
        >
          <Plus size={14} /> Add Prospect
        </button>
      </div>

      {/* Bulk actions */}
      {selected.length > 0 && (
        <div className="flex items-center gap-3 mb-3 bg-amber-400/10 border border-amber-400/30 rounded-lg px-4 py-2">
          <span className="text-amber-400 text-sm font-medium">{selected.length} selected</span>
          <button onClick={exportSelected} className="text-xs text-white bg-gray-700 hover:bg-gray-600 px-3 py-1 rounded transition flex items-center gap-1">
            <Upload size={12} /> Export CSV
          </button>
          <button onClick={() => setSelected([])} className="text-xs text-gray-400 hover:text-white ml-auto">
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-800 text-gray-400 text-xs uppercase">
                <th className="px-4 py-3 text-left w-10">
                  <input
                    type="checkbox"
                    checked={selected.length === filtered.length && filtered.length > 0}
                    onChange={toggleAll}
                    className="accent-amber-400"
                  />
                </th>
                {[
                  { col: 'last_name', label: 'Name' },
                  { col: 'phone', label: 'Phone' },
                  { col: 'stage', label: 'Stage' },
                  { col: 'lead_source', label: 'Source' },
                  { col: 'insurance_type', label: 'Insurance' },
                  { col: 't65_date', label: 'T65 Date' },
                  { col: 'tcpa_consent', label: 'TCPA' },
                  { col: 'days_in_stage', label: 'Days in Stage' },
                  { col: 'created_at', label: 'Added' },
                ].map(({ col, label }) => (
                  <th
                    key={col}
                    className="px-4 py-3 text-left cursor-pointer hover:text-white transition"
                    onClick={() => toggleSort(col)}
                  >
                    {label}<SortIcon col={col} />
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr>
                  <td colSpan={10} className="text-center text-gray-600 py-12">No prospects found.</td>
                </tr>
              )}
              {filtered.map(p => {
                const stage    = STAGES.find(s => s.key === p.stage);
                const insType  = INSURANCE_TYPES[p.insurance_type] || INSURANCE_TYPES.other;
                const daysIn   = p.days_in_stage ?? 0;
                return (
                  <tr
                    key={p.id}
                    className="border-b border-gray-800 hover:bg-gray-800/50 cursor-pointer transition"
                    onClick={() => onProspectClick(p)}
                  >
                    <td className="px-4 py-3" onClick={e => { e.stopPropagation(); toggleSelect(p.id); }}>
                      <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleSelect(p.id)} className="accent-amber-400" />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-medium text-white">{p.first_name} {p.last_name}</div>
                      {p.email && <div className="text-gray-500 text-xs">{p.email}</div>}
                    </td>
                    <td className="px-4 py-3 text-gray-300">{fmtPhone(p.phone)}</td>
                    <td className="px-4 py-3">
                      {stage && (
                        <span className={`px-2 py-0.5 rounded text-xs font-medium ${stage.color}`}>
                          {stage.label}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-400">{sourceLabel(p.lead_source)}</td>
                    <td className="px-4 py-3">
                      <span className={`px-1.5 py-0.5 rounded text-xs border ${insType.color}`}>
                        {insType.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-400">{fmtDate(p.t65_date)}</td>
                    <td className="px-4 py-3">
                      {p.tcpa_consent
                        ? <Shield size={14} className="text-green-400" />
                        : <AlertTriangle size={14} className="text-red-400" />
                      }
                    </td>
                    <td className={`px-4 py-3 ${daysInStageColor(daysIn)}`}>{daysIn}d</td>
                    <td className="px-4 py-3 text-gray-500">{fmtDate(p.created_at)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// CampaignsTab
// ---------------------------------------------------------------------------
function CampaignsTab({ campaigns, onReload, creditBalance, onCreditsChanged }) {
  const [showNew, setShowNew]         = useState(false);
  const [showBuyCredits, setShowBuyCredits] = useState(false);

  const STATUS_COLORS = {
    active:   'bg-green-900/40 text-green-400 border-green-500/30',
    paused:   'bg-amber-900/40 text-amber-400 border-amber-500/30',
    draft:    'bg-gray-800 text-gray-400 border-gray-600',
    completed:'bg-blue-900/40 text-blue-400 border-blue-500/30',
  };

  return (
    <div>
      {/* SMS Credits Card — shown first so agents know what's needed before creating a campaign */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 mb-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-10 h-10 bg-amber-400/10 rounded-lg flex items-center justify-center flex-shrink-0">
              <DollarSign size={20} className="text-amber-400" />
            </div>
            <div>
              <p className="text-white font-semibold text-sm">SMS Credits</p>
              <p className="text-gray-500 text-xs mt-0.5">
                Campaigns require credits or a card charge to send SMS messages to prospects.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <p className="text-2xl font-bold text-white">
                {creditBalance !== null ? creditBalance.toLocaleString() : '—'}
              </p>
              <p className="text-gray-500 text-xs">credits available</p>
            </div>
            <button
              onClick={() => setShowBuyCredits(true)}
              className="bg-amber-400 text-gray-900 font-semibold px-4 py-2 rounded-lg hover:bg-amber-300 transition text-sm flex items-center gap-2 whitespace-nowrap"
            >
              <Plus size={14} /> Buy Credits
            </button>
          </div>
        </div>

        {/* Pricing summary */}
        <div className="mt-4 pt-4 border-t border-gray-800 grid grid-cols-4 gap-3">
          {[
            { label: 'Starter',  credits: '500',   price: '$25',  rate: '$0.05/SMS' },
            { label: 'Growth',   credits: '2,000', price: '$70',  rate: '$0.035/SMS' },
            { label: 'Pro',      credits: '5,000', price: '$150', rate: '$0.03/SMS' },
            { label: 'Agency',   credits: '10,000',price: '$250', rate: '$0.025/SMS' },
          ].map(b => (
            <div key={b.label} className="bg-gray-800/60 rounded-lg px-3 py-2 text-center">
              <p className="text-gray-400 text-xs font-medium">{b.label}</p>
              <p className="text-white font-bold text-sm mt-0.5">{b.credits} credits</p>
              <p className="text-amber-400 text-xs font-semibold">{b.price}</p>
              <p className="text-gray-600 text-xs">{b.rate}</p>
            </div>
          ))}
        </div>

        <p className="text-gray-600 text-xs mt-3 text-center">
          Or charge per campaign at launch · Messages drip 8am–8pm with random jitter to protect deliverability
        </p>
      </div>

      {/* CMS Compliance Banner */}
      <div className="flex items-start gap-3 bg-amber-400/10 border border-amber-400/30 rounded-xl px-4 py-3 mb-5">
        <AlertTriangle size={18} className="text-amber-400 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-amber-300 text-sm font-semibold">CMS Compliance Notice</p>
          <p className="text-amber-200/70 text-xs mt-0.5">
            CMS prohibits unsolicited Medicare outreach. You must have documented consent (TCPA) before contacting
            Medicare beneficiaries. Always verify consent status before launching any campaign.
          </p>
        </div>
      </div>

      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-white font-semibold">Outreach Campaigns</h2>
        <button
          onClick={() => setShowNew(true)}
          className="bg-amber-400 text-gray-900 font-semibold px-4 py-2 rounded-lg hover:bg-amber-300 transition text-sm flex items-center gap-2"
        >
          <Plus size={14} /> New Campaign
        </button>
      </div>

      {/* Campaign list */}
      <div className="flex flex-col gap-3">
        {campaigns.length === 0 && (
          <div className="bg-gray-900 border border-gray-800 rounded-xl py-12 text-center">
            <Zap size={32} className="text-gray-700 mx-auto mb-3" />
            <p className="text-gray-500 text-sm">No campaigns yet. Create your first campaign to start nurturing prospects.</p>
          </div>
        )}
        {campaigns.map(c => (
          <div key={c.id} className="bg-gray-900 border border-gray-800 rounded-xl px-5 py-4 flex items-center gap-5">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 mb-1">
                <span className="text-white font-semibold text-sm">{c.name}</span>
                <span className={`px-2 py-0.5 rounded text-xs border ${STATUS_COLORS[c.status] || STATUS_COLORS.draft}`}>
                  {(c.status || 'draft').charAt(0).toUpperCase() + (c.status || 'draft').slice(1)}
                </span>
                <span className="bg-gray-800 text-gray-400 text-xs px-2 py-0.5 rounded">
                  {(c.campaign_type || 'sms').toUpperCase()}
                </span>
              </div>
              <p className="text-gray-500 text-xs">
                {c.steps_count || 0} steps &nbsp;&bull;&nbsp; {c.enrolled_count || 0} enrolled &nbsp;&bull;&nbsp; {c.sent_count || 0} sent &nbsp;&bull;&nbsp; {c.reply_count || 0} replies
              </p>
            </div>
            <div className="flex items-center gap-2">
              {c.campaign_type === 'sms' && (
                <span className="text-xs text-amber-400/70 flex items-center gap-1">
                  <AlertCircle size={12} /> Requires Twilio
                </span>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Direct mail placeholder */}
      <div className="mt-6 bg-gray-900 border border-dashed border-gray-700 rounded-xl px-5 py-6 flex items-center gap-4">
        <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
          <FileText size={20} className="text-gray-500" />
        </div>
        <div className="flex-1">
          <p className="text-white text-sm font-semibold">Direct Mail Integration</p>
          <p className="text-gray-500 text-xs mt-0.5">Connect a direct mail vendor to send physical mailers to prospects automatically.</p>
        </div>
        <span className="text-xs text-gray-600 bg-gray-800 px-3 py-1 rounded-full">Coming Soon</span>
      </div>

      {/* New Campaign Modal */}
      {showNew && (
        <NewCampaignModal
          onClose={() => setShowNew(false)}
          onSave={() => { onReload(); setShowNew(false); }}
          creditBalance={creditBalance}
          onCreditsChanged={onCreditsChanged}
        />
      )}

      {/* Buy Credits Modal */}
      {showBuyCredits && (
        <BuyCreditsModal
          onClose={() => setShowBuyCredits(false)}
          onPurchased={() => { onCreditsChanged(); setShowBuyCredits(false); }}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// BuyCreditsModal
// ---------------------------------------------------------------------------
const CREDIT_BUNDLES = [
  { credits: 500,   price: 25,  label: 'Starter',  description: 'Great for testing' },
  { credits: 2000,  price: 70,  label: 'Growth',   description: '$0.035/SMS'        },
  { credits: 5000,  price: 150, label: 'Pro',       description: '$0.03/SMS'         },
  { credits: 10000, price: 250, label: 'Agency',   description: '$0.025/SMS'        },
];

function BuyCreditsModal({ onClose }) {
  const [selected, setSelected] = useState(null);
  const [buying, setBuying]     = useState(false);
  const [error, setError]       = useState('');

  async function handleBuy() {
    if (!selected) return;
    setBuying(true);
    setError('');
    try {
      const r = await fetch(`${BASE}/api/prospecting/credits/buy`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ bundleSize: selected }),
      });
      const j = await r.json();
      if (j.ok && j.checkoutUrl) {
        window.location.href = j.checkoutUrl;
      } else {
        setError(j.error || 'Failed to start checkout');
      }
    } catch {
      setError('Network error — please try again');
    } finally {
      setBuying(false);
    }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg">
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h3 className="text-white font-semibold flex items-center gap-2">
            <DollarSign size={16} className="text-amber-400" /> Buy SMS Credits
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white"><X size={20} /></button>
        </div>

        <div className="p-6">
          <p className="text-gray-400 text-xs mb-4">Credits are shared across all campaigns. They never expire.</p>
          <div className="grid grid-cols-2 gap-3 mb-5">
            {CREDIT_BUNDLES.map(b => (
              <button
                key={b.credits}
                onClick={() => setSelected(b.credits)}
                className={`flex flex-col items-start p-4 rounded-xl border transition text-left ${
                  selected === b.credits
                    ? 'border-amber-400 bg-amber-400/10'
                    : 'border-gray-700 bg-gray-800 hover:border-gray-500'
                }`}
              >
                <span className={`text-xs font-semibold mb-1 ${selected === b.credits ? 'text-amber-400' : 'text-gray-400'}`}>
                  {b.label}
                </span>
                <span className="text-white font-bold text-lg">{b.credits.toLocaleString()} credits</span>
                <span className="text-gray-500 text-xs mt-0.5">{b.description}</span>
                <span className="text-amber-400 font-semibold text-sm mt-2">${b.price}</span>
              </button>
            ))}
          </div>

          {error && <p className="text-red-400 text-xs mb-3">{error}</p>}

          <button
            onClick={handleBuy}
            disabled={!selected || buying}
            className="w-full bg-amber-400 text-gray-900 font-semibold py-2.5 rounded-lg hover:bg-amber-300 transition text-sm disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {buying ? 'Redirecting to checkout…' : selected ? `Buy ${selected.toLocaleString()} Credits →` : 'Select a bundle'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// NewCampaignModal (multi-step)
// ---------------------------------------------------------------------------
function NewCampaignModal({ onClose, onSave, creditBalance, onCreditsChanged }) {
  const [step, setStep]           = useState(1);
  const [saving, setSaving]       = useState(false);
  const [campaignId, setCampaignId] = useState(null); // set after step-5 save
  const [form, setForm]           = useState({
    name: '',
    campaign_type: 'sms',
    target_stages: [],
    require_tcpa: true,
    steps: [{ delay_days: 0, channel: 'sms', content: '' }],
  });
  const [audienceCount, setAudienceCount] = useState(null);
  const [estimate, setEstimate]   = useState(null);  // { smsCount, costCents }
  const [billingError, setBillingError] = useState('');
  const [activating, setActivating]    = useState(false);

  function setField(k, v) { setForm(f => ({ ...f, [k]: v })); }

  function toggleStage(key) {
    setForm(f => ({
      ...f,
      target_stages: f.target_stages.includes(key)
        ? f.target_stages.filter(s => s !== key)
        : [...f.target_stages, key],
    }));
  }

  function addStep() {
    setForm(f => ({
      ...f,
      steps: [...f.steps, { delay_days: 1, channel: form.campaign_type === 'email' ? 'email' : 'sms', content: '' }]
    }));
  }

  function removeStep(i) {
    setForm(f => ({ ...f, steps: f.steps.filter((_, idx) => idx !== i) }));
  }

  function updateStep(i, k, v) {
    setForm(f => {
      const steps = [...f.steps];
      steps[i] = { ...steps[i], [k]: v };
      return { ...f, steps };
    });
  }

  async function fetchAudience() {
    try {
      const r = await fetch(`${BASE}/api/prospecting/campaigns/audience-preview?stages=${form.target_stages.join(',')}&require_tcpa=${form.require_tcpa}`, { credentials: 'include' });
      const j = await r.json();
      setAudienceCount(j.count ?? 0);
    } catch { setAudienceCount(null); }
  }

  useEffect(() => {
    if (step === 5) fetchAudience();
    if (step === 6 && campaignId) fetchEstimate(campaignId);
  }, [step]);

  async function fetchEstimate(id) {
    try {
      const r = await fetch(`${BASE}/api/prospecting/campaigns/${id}/billing-estimate`, { credentials: 'include' });
      const j = await r.json();
      if (j.ok) setEstimate(j);
    } catch {}
  }

  // Step 5→6 transition: save the campaign first, then show billing step
  async function handleSaveDraft() {
    setSaving(true);
    try {
      const r = await fetch(`${BASE}/api/prospecting/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name:          form.name,
          campaign_type: form.campaign_type,
          target_stages: form.target_stages,
          requires_tcpa: form.require_tcpa,
          sequence_steps: form.steps,
          channel:       form.campaign_type,
          status:        'draft',
        }),
      });
      const j = await r.json();
      if (j.ok && j.data?.id) {
        setCampaignId(j.data.id);
        setStep(6);
      }
    } finally { setSaving(false); }
  }

  async function handleActivate(paymentMethod) {
    if (!campaignId) return;
    setActivating(true);
    setBillingError('');
    try {
      const r = await fetch(`${BASE}/api/prospecting/campaigns/${campaignId}/pay-and-activate`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ paymentMethod }),
      });
      const j = await r.json();
      if (j.ok) {
        if (j.checkoutUrl) {
          window.location.href = j.checkoutUrl;
        } else {
          if (onCreditsChanged) onCreditsChanged();
          onSave();
        }
      } else if (r.status === 402) {
        setBillingError(`Insufficient credits — need ${j.needed}, have ${j.balance}. Buy more credits first.`);
      } else {
        setBillingError(j.error || 'Failed to activate campaign');
      }
    } catch {
      setBillingError('Network error — please try again');
    } finally {
      setActivating(false);
    }
  }

  // Legacy fallback (no billing) - kept for direct-mail / free campaigns
  async function handleCreate() {
    setSaving(true);
    try {
      const r = await fetch(`${BASE}/api/prospecting/campaigns`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          name:          form.name,
          campaign_type: form.campaign_type,
          target_stages: form.target_stages,
          requires_tcpa: form.require_tcpa,
          sequence_steps: form.steps,
          channel:       form.campaign_type,
        }),
      });
      if (r.ok) onSave();
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-lg max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h3 className="text-white font-semibold">New Campaign — Step {step} of 6</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition"><X size={20} /></button>
        </div>

        {/* Step progress */}
        <div className="px-6 pt-3">
          <div className="flex gap-1">
            {[1,2,3,4,5,6].map(n => (
              <div key={n} className={`h-1 flex-1 rounded-full transition ${n <= step ? 'bg-amber-400' : 'bg-gray-700'}`} />
            ))}
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {/* Step 1: Name + type */}
          {step === 1 && (
            <div className="flex flex-col gap-4">
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Campaign Name *</label>
                <input
                  value={form.name}
                  onChange={e => setField('name', e.target.value)}
                  placeholder="e.g. T65 Welcome Series"
                  className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-400"
                />
              </div>
              <div>
                <label className="text-gray-400 text-xs mb-1 block">Campaign Type *</label>
                <div className="grid grid-cols-2 gap-2">
                  {[
                    { val: 'sms', label: 'SMS', icon: <MessageSquare size={16} /> },
                    { val: 'email', label: 'Email', icon: <Mail size={16} /> },
                    { val: 'direct_mail', label: 'Direct Mail', icon: <FileText size={16} /> },
                    { val: 'combined', label: 'Combined', icon: <Zap size={16} /> },
                  ].map(t => (
                    <button
                      key={t.val}
                      onClick={() => setField('campaign_type', t.val)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition ${
                        form.campaign_type === t.val
                          ? 'bg-amber-400/10 border-amber-400 text-amber-400'
                          : 'bg-gray-800 border-gray-700 text-gray-400 hover:border-gray-500'
                      }`}
                    >
                      {t.icon} {t.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Target stages */}
          {step === 2 && (
            <div>
              <p className="text-gray-400 text-xs mb-3">Select which pipeline stages to enroll prospects from:</p>
              <div className="flex flex-col gap-2">
                {STAGES.map(s => (
                  <button
                    key={s.key}
                    onClick={() => toggleStage(s.key)}
                    className={`flex items-center justify-between px-4 py-3 rounded-lg border transition ${
                      form.target_stages.includes(s.key)
                        ? `${s.border} bg-gray-800`
                        : 'border-gray-700 bg-gray-800/50 hover:border-gray-600'
                    }`}
                  >
                    <span className={`text-sm font-medium ${s.color}`}>{s.label}</span>
                    {form.target_stages.includes(s.key)
                      ? <CheckCircle2 size={16} className="text-amber-400" />
                      : <div className="w-4 h-4 border border-gray-600 rounded-full" />
                    }
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Step 3: TCPA */}
          {step === 3 && (
            <div>
              <div className="flex items-center justify-between bg-gray-800 border border-gray-700 rounded-xl px-4 py-4 mb-4">
                <div>
                  <p className="text-white text-sm font-semibold">Require TCPA Consent</p>
                  <p className="text-gray-500 text-xs mt-0.5">Only send to prospects with verified TCPA consent.</p>
                </div>
                <button
                  onClick={() => setField('require_tcpa', !form.require_tcpa)}
                  className={`relative w-11 h-6 rounded-full transition ${form.require_tcpa ? 'bg-amber-400' : 'bg-gray-600'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-5 h-5 bg-white rounded-full shadow transition-transform ${form.require_tcpa ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>

              {!form.require_tcpa && (
                <div className="flex items-start gap-3 bg-amber-400/10 border border-amber-400/30 rounded-xl px-4 py-3">
                  <AlertTriangle size={16} className="text-amber-400 flex-shrink-0 mt-0.5" />
                  <p className="text-amber-300 text-xs">
                    Warning: Sending to prospects without TCPA consent may violate federal law and CMS regulations.
                    You are solely responsible for compliance. It is strongly recommended to require consent.
                  </p>
                </div>
              )}
            </div>
          )}

          {/* Step 4: Sequence builder */}
          {step === 4 && (
            <div>
              <p className="text-gray-400 text-xs mb-3">Define the message sequence for this campaign:</p>
              <div className="flex flex-col gap-3">
                {form.steps.map((s, i) => (
                  <div key={i} className="bg-gray-800 border border-gray-700 rounded-xl p-4">
                    <div className="flex items-center gap-3 mb-3">
                      <div className="w-6 h-6 bg-amber-400 text-gray-900 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0">
                        {i + 1}
                      </div>
                      <div className="flex gap-2 flex-1">
                        <div className="flex items-center gap-1.5">
                          <span className="text-gray-500 text-xs">Day</span>
                          <input
                            type="number"
                            min={0}
                            value={s.delay_days}
                            onChange={e => updateStep(i, 'delay_days', parseInt(e.target.value) || 0)}
                            className="w-14 bg-gray-900 border border-gray-600 rounded px-2 py-1 text-sm text-white text-center focus:outline-none focus:border-amber-400"
                          />
                        </div>
                        <select
                          value={s.channel}
                          onChange={e => updateStep(i, 'channel', e.target.value)}
                          className="bg-gray-900 border border-gray-600 rounded px-2 py-1 text-sm text-white focus:outline-none focus:border-amber-400"
                        >
                          <option value="sms">SMS</option>
                          <option value="email">Email</option>
                        </select>
                      </div>
                      {form.steps.length > 1 && (
                        <button onClick={() => removeStep(i)} className="text-gray-600 hover:text-red-400 transition">
                          <X size={14} />
                        </button>
                      )}
                    </div>
                    <textarea
                      value={s.content}
                      onChange={e => updateStep(i, 'content', e.target.value)}
                      placeholder="Message content..."
                      rows={3}
                      className="w-full bg-gray-900 border border-gray-600 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 resize-none"
                    />
                    <p className="text-gray-600 text-xs mt-1">{s.content.length} chars</p>
                  </div>
                ))}
                <button
                  onClick={addStep}
                  className="border border-dashed border-gray-600 hover:border-amber-400 text-gray-500 hover:text-amber-400 rounded-xl py-3 text-sm transition flex items-center justify-center gap-2"
                >
                  <Plus size={14} /> Add Step
                </button>
              </div>
            </div>
          )}

          {/* Step 5: Audience preview */}
          {step === 5 && (
            <div>
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-5 mb-4 text-center">
                <p className="text-gray-400 text-xs mb-2">Estimated Audience</p>
                <p className="text-4xl font-bold text-white">{audienceCount ?? '—'}</p>
                <p className="text-gray-500 text-xs mt-1">prospects matching your criteria</p>
              </div>
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 text-sm text-gray-300 space-y-2">
                <div className="flex justify-between"><span className="text-gray-500">Name</span><span className="text-white font-medium">{form.name}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Type</span><span>{form.campaign_type.toUpperCase()}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Stages</span><span>{form.target_stages.length > 0 ? form.target_stages.map(k => STAGES.find(s=>s.key===k)?.label).join(', ') : 'All'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">TCPA Required</span><span>{form.require_tcpa ? 'Yes' : 'No'}</span></div>
                <div className="flex justify-between"><span className="text-gray-500">Steps</span><span>{form.steps.length}</span></div>
              </div>
            </div>
          )}

          {/* Step 6: Billing & Launch */}
          {step === 6 && (
            <div>
              {/* Summary */}
              <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 mb-4 text-center">
                <p className="text-gray-400 text-xs mb-1">Campaign will send</p>
                <p className="text-3xl font-bold text-white">{estimate?.smsCount ?? '—'} SMS</p>
                {estimate && (
                  <p className="text-gray-500 text-xs mt-1">
                    Pay-per-send price: ${(estimate.costCents / 100).toFixed(2)}
                  </p>
                )}
              </div>

              {/* Payment options */}
              <div className="grid grid-cols-2 gap-3 mb-4">
                {/* Use Credits */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 flex flex-col gap-2">
                  <p className="text-white text-sm font-semibold">Use Credits</p>
                  <p className="text-gray-500 text-xs">
                    Balance: <span className={`font-semibold ${(creditBalance ?? 0) >= (estimate?.smsCount ?? 0) ? 'text-emerald-400' : 'text-red-400'}`}>
                      {(creditBalance ?? 0).toLocaleString()}
                    </span>
                  </p>
                  <p className="text-gray-500 text-xs">Need: {(estimate?.smsCount ?? 0).toLocaleString()}</p>
                  {(creditBalance ?? 0) >= (estimate?.smsCount ?? 0) ? (
                    <span className="text-emerald-400 text-xs">✓ Sufficient balance</span>
                  ) : (
                    <span className="text-red-400 text-xs">⚠ Insufficient — buy more</span>
                  )}
                  <button
                    onClick={() => handleActivate('credits')}
                    disabled={activating || (creditBalance ?? 0) < (estimate?.smsCount ?? 0)}
                    className="mt-auto bg-amber-400 text-gray-900 font-semibold px-3 py-2 rounded-lg hover:bg-amber-300 transition text-xs disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    {activating ? 'Launching…' : 'Launch with Credits'}
                  </button>
                </div>

                {/* Pay Now */}
                <div className="bg-gray-800 border border-gray-700 rounded-xl p-4 flex flex-col gap-2">
                  <p className="text-white text-sm font-semibold">Pay Now</p>
                  <p className="text-gray-500 text-xs">Charged via card</p>
                  {estimate && (
                    <p className="text-amber-400 font-bold text-lg">${(estimate.costCents / 100).toFixed(2)}</p>
                  )}
                  <p className="text-gray-600 text-xs">Tiered rate: $0.05→$0.04→$0.03</p>
                  <button
                    onClick={() => handleActivate('pay_per_sms')}
                    disabled={activating}
                    className="mt-auto bg-blue-600 text-white font-semibold px-3 py-2 rounded-lg hover:bg-blue-500 transition text-xs disabled:opacity-40"
                  >
                    {activating ? 'Redirecting…' : 'Pay & Launch →'}
                  </button>
                </div>
              </div>

              {billingError && (
                <div className="bg-red-900/30 border border-red-500/30 rounded-lg px-3 py-2 text-red-400 text-xs">
                  {billingError}
                </div>
              )}

              <p className="text-gray-600 text-xs text-center mt-3">
                ℹ️ Messages drip over campaign duration (8am–8pm), with per-prospect jitter to avoid carrier flags.
              </p>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800">
          <button
            onClick={() => {
              if (step > 1 && step < 6) setStep(s => s - 1);
              else if (step === 6) setStep(5);
              else onClose();
            }}
            className="text-gray-400 hover:text-white text-sm transition"
          >
            {step > 1 ? '← Back' : 'Cancel'}
          </button>
          {step < 5 ? (
            <button
              onClick={() => setStep(s => s + 1)}
              disabled={step === 1 && !form.name.trim()}
              className="bg-amber-400 text-gray-900 font-semibold px-5 py-2 rounded-lg hover:bg-amber-300 transition text-sm disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next →
            </button>
          ) : step === 5 ? (
            <button
              onClick={handleSaveDraft}
              disabled={saving}
              className="bg-amber-400 text-gray-900 font-semibold px-5 py-2 rounded-lg hover:bg-amber-300 transition text-sm disabled:opacity-50 flex items-center gap-2"
            >
              {saving ? <><RefreshCw size={14} className="animate-spin" /> Saving…</> : 'Next: Billing →'}
            </button>
          ) : null /* Step 6 buttons are in the body */}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// LeadSourcesTab
// ---------------------------------------------------------------------------
function LeadSourcesTab({ prospects }) {
  const [webhookUrl, setWebhookUrl] = useState('');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    fetch(`${BASE}/api/prospecting/webhooks/info`, { credentials: 'include' })
      .then(r => r.json())
      .then(j => setWebhookUrl(j.webhook_url || ''))
      .catch(() => {});
  }, []);

  function copyUrl() {
    navigator.clipboard.writeText(webhookUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  // Compute source stats from prospect list
  const sourceStats = LEAD_SOURCES.map(src => {
    const inSource = prospects.filter(p => p.lead_source === src);
    const wonCount = inSource.filter(p => p.stage === 'closed_won').length;
    return {
      source: sourceLabel(src),
      total: inSource.length,
      won: wonCount,
      rate: inSource.length > 0 ? Math.round((wonCount / inSource.length) * 100) : 0,
    };
  }).filter(s => s.total > 0).sort((a, b) => b.total - a.total);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Conversion by source chart */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
          <BarChart3 size={16} className="text-amber-400" /> Leads by Source
        </h3>
        {sourceStats.length === 0 ? (
          <div className="text-center py-8 text-gray-600 text-sm">No source data yet.</div>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={sourceStats} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="source" tick={{ fill: '#9CA3AF', fontSize: 11 }} />
              <YAxis tick={{ fill: '#9CA3AF', fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: '#1F2937', border: '1px solid #374151', borderRadius: 8 }}
                labelStyle={{ color: '#F9FAFB' }}
              />
              <Bar dataKey="total" name="Total Leads" fill="#FBBF24" radius={[3,3,0,0]} />
              <Bar dataKey="won" name="Closed Won" fill="#10B981" radius={[3,3,0,0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Conversion rates table */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
          <TrendingUp size={16} className="text-amber-400" /> Conversion Rates
        </h3>
        <div className="flex flex-col gap-2">
          {sourceStats.length === 0 && (
            <p className="text-gray-600 text-sm text-center py-6">No data yet.</p>
          )}
          {sourceStats.map(s => (
            <div key={s.source} className="flex items-center gap-3">
              <span className="text-gray-400 text-xs w-28 flex-shrink-0">{s.source}</span>
              <div className="flex-1 bg-gray-800 rounded-full h-2">
                <div
                  className="bg-amber-400 rounded-full h-2 transition-all"
                  style={{ width: `${s.rate}%` }}
                />
              </div>
              <span className="text-white text-xs w-10 text-right">{s.rate}%</span>
              <span className="text-gray-600 text-xs w-16 text-right">{s.won}/{s.total}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Facebook Lead Ads webhook */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <Globe size={16} className="text-blue-400" />
          <h3 className="text-white font-semibold text-sm">Facebook Lead Ads</h3>
        </div>
        <p className="text-gray-500 text-xs mb-4">
          Connect Facebook Lead Ad forms to automatically create prospects when someone fills out your ad.
        </p>
        <div className="mb-3">
          <p className="text-gray-400 text-xs mb-1">Webhook URL</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-xs text-amber-300 font-mono truncate">
              {webhookUrl || `${BASE}/api/prospecting/webhooks/facebook`}
            </code>
            <button
              onClick={copyUrl}
              className={`flex-shrink-0 p-2 rounded-lg border transition ${copied ? 'border-green-500 text-green-400' : 'border-gray-700 text-gray-400 hover:text-white hover:border-gray-500'}`}
              title="Copy webhook URL"
            >
              {copied ? <Check size={14} /> : <Copy size={14} />}
            </button>
          </div>
        </div>
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 text-xs text-gray-400 space-y-1">
          <p className="font-semibold text-gray-300">Setup Instructions:</p>
          <p>1. Go to Facebook Events Manager → Webhooks</p>
          <p>2. Click "Add Webhook" and paste the URL above</p>
          <p>3. Subscribe to the "leads" event</p>
          <p>4. Enter your Page ID in the agency settings</p>
          <p>5. New leads will appear automatically in the Pipeline tab</p>
        </div>
      </div>

      {/* Web form webhook */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <div className="flex items-center gap-2 mb-1">
          <Globe size={16} className="text-purple-400" />
          <h3 className="text-white font-semibold text-sm">Web Form Webhook</h3>
        </div>
        <p className="text-gray-500 text-xs mb-4">
          Use this webhook endpoint to capture leads from any web form (contact forms, landing pages, etc).
        </p>
        <div className="mb-3">
          <p className="text-gray-400 text-xs mb-1">POST to this URL</p>
          <div className="flex items-center gap-2">
            <code className="flex-1 bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-xs text-amber-300 font-mono truncate">
              {`${BASE}/api/prospecting/webhooks/web-form`}
            </code>
            <button
              onClick={() => {
                navigator.clipboard.writeText(`${BASE}/api/prospecting/webhooks/web-form`);
                setCopied(true);
                setTimeout(() => setCopied(false), 2000);
              }}
              className="flex-shrink-0 p-2 rounded-lg border border-gray-700 text-gray-400 hover:text-white hover:border-gray-500 transition"
            >
              <Copy size={14} />
            </button>
          </div>
        </div>
        <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 text-xs text-gray-400 space-y-1">
          <p className="font-semibold text-gray-300">Expected JSON payload:</p>
          <pre className="text-green-400 font-mono text-xs mt-1">{`{
  "first_name": "Jane",
  "last_name": "Smith",
  "phone": "5551234567",
  "email": "jane@example.com",
  "t65_date": "2025-09-01"
}`}</pre>
        </div>
      </div>

      {/* Google Ads placeholder */}
      <div className="bg-gray-900 border border-dashed border-gray-700 rounded-xl p-5 flex items-center gap-4">
        <div className="w-10 h-10 bg-gray-800 rounded-lg flex items-center justify-center">
          <Search size={20} className="text-gray-500" />
        </div>
        <div className="flex-1">
          <p className="text-white text-sm font-semibold">Google Ads Lead Forms</p>
          <p className="text-gray-500 text-xs mt-0.5">Automatically import leads from Google Ads lead form extensions.</p>
        </div>
        <span className="text-xs text-gray-600 bg-gray-800 px-3 py-1 rounded-full">Coming Soon</span>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AnalyticsTab
// ---------------------------------------------------------------------------
function AnalyticsTab({ analytics, prospects }) {
  // Compute funnel from prospects
  const stageCounts = STAGES.map(s => ({
    label: s.label,
    key: s.key,
    count: prospects.filter(p => p.stage === s.key).length,
    color: s.color,
    bg: s.bg,
  }));

  // Avg days per stage
  const avgDaysData = STAGES.slice(0, 4).map(s => {
    const inStage = prospects.filter(p => p.stage === s.key && p.days_in_stage != null);
    const avg = inStage.length ? Math.round(inStage.reduce((a,c) => a + c.days_in_stage, 0) / inStage.length) : 0;
    return { stage: s.label, avg_days: avg };
  });

  // T65 pipeline
  const now = new Date();
  function countT65(months) {
    const future = new Date(now.getTime() + months * 30.5 * 86400000);
    return prospects.filter(p => {
      if (!p.t65_date) return false;
      const d = new Date(p.t65_date);
      return d >= now && d <= future;
    }).length;
  }

  const t65Buckets = [
    { label: '1 Month', count: countT65(1) },
    { label: '2 Months', count: countT65(2) - countT65(1) },
    { label: '3 Months', count: countT65(3) - countT65(2) },
    { label: '6 Months', count: countT65(6) - countT65(3) },
  ];

  // Weekly velocity from analytics prop or empty
  const velocityData = analytics?.weekly_velocity || [];

  // Conversion: Lead -> Qualified -> Appt Set -> Closed Won
  const funnelStages = ['lead','qualified','appointment_set','closed_won'];
  const funnelCounts = funnelStages.map(k => prospects.filter(p => p.stage === k || p.stage === 'closed_won' || p.stage === 'closed_lost').length);
  // Simple cumulative funnel
  const funnelData = [
    { stage: 'Lead',        count: prospects.length },
    { stage: 'Qualified',   count: prospects.filter(p => ['qualified','appointment_set','follow_up','closed_won','closed_lost'].includes(p.stage)).length },
    { stage: 'Appt Set',    count: prospects.filter(p => ['appointment_set','closed_won','closed_lost'].includes(p.stage)).length },
    { stage: 'Closed Won',  count: prospects.filter(p => p.stage === 'closed_won').length },
  ];

  const totalLeads = prospects.length || 1;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Conversion funnel */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 lg:col-span-2">
        <h3 className="text-white font-semibold text-sm mb-5 flex items-center gap-2">
          <TrendingUp size={16} className="text-amber-400" /> Conversion Funnel
        </h3>
        <div className="flex items-end gap-4">
          {funnelData.map((f, i) => {
            const pct = Math.round((f.count / totalLeads) * 100);
            const gateDropPct = i > 0
              ? Math.round(((funnelData[i-1].count - f.count) / Math.max(funnelData[i-1].count, 1)) * 100)
              : 0;
            return (
              <React.Fragment key={f.stage}>
                <div className="flex-1 flex flex-col items-center gap-2">
                  <div className="flex flex-col items-center">
                    <span className="text-2xl font-bold text-white">{f.count}</span>
                    <span className="text-gray-500 text-xs">{pct}% of total</span>
                  </div>
                  <div
                    className="w-full bg-amber-400/20 border border-amber-400/40 rounded-lg flex items-end justify-center"
                    style={{ height: 80, minHeight: 20 }}
                  >
                    <div
                      className="w-full bg-amber-400 rounded-lg transition-all"
                      style={{ height: `${Math.max(pct, 5)}%` }}
                    />
                  </div>
                  <span className="text-gray-400 text-xs font-medium">{f.stage}</span>
                </div>
                {i < funnelData.length - 1 && (
                  <div className="flex flex-col items-center self-center pb-8">
                    <ArrowRight size={16} className="text-gray-600" />
                    {gateDropPct > 0 && (
                      <span className="text-red-400 text-xs mt-1">-{gateDropPct}%</span>
                    )}
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>

      {/* Avg days per stage */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
          <Clock size={16} className="text-amber-400" /> Avg Days per Stage
        </h3>
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={avgDaysData} layout="vertical" margin={{ left: 10, right: 20, top: 0, bottom: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#374151" horizontal={false} />
            <XAxis type="number" tick={{ fill: '#9CA3AF', fontSize: 11 }} />
            <YAxis type="category" dataKey="stage" tick={{ fill: '#9CA3AF', fontSize: 11 }} width={70} />
            <Tooltip
              contentStyle={{ background: '#1F2937', border: '1px solid #374151', borderRadius: 8 }}
              formatter={(v) => [`${v} days`, 'Avg']}
            />
            <Bar dataKey="avg_days" name="Avg Days" fill="#FBBF24" radius={[0,3,3,0]} />
          </BarChart>
        </ResponsiveContainer>
      </div>

      {/* Weekly velocity */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5">
        <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
          <Activity size={16} className="text-amber-400" /> Weekly Velocity
        </h3>
        {velocityData.length === 0 ? (
          <div className="flex items-center justify-center h-48 text-gray-600 text-sm">
            Not enough data yet.
          </div>
        ) : (
          <ResponsiveContainer width="100%" height={220}>
            <AreaChart data={velocityData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="newLeadsGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#FBBF24" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#FBBF24" stopOpacity={0} />
                </linearGradient>
                <linearGradient id="closedGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10B981" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
              <XAxis dataKey="week" tick={{ fill: '#9CA3AF', fontSize: 11 }} />
              <YAxis tick={{ fill: '#9CA3AF', fontSize: 11 }} />
              <Tooltip
                contentStyle={{ background: '#1F2937', border: '1px solid #374151', borderRadius: 8 }}
              />
              <Area type="monotone" dataKey="new_leads" name="New Leads" stroke="#FBBF24" fill="url(#newLeadsGrad)" strokeWidth={2} />
              <Area type="monotone" dataKey="closed_won" name="Closed Won" stroke="#10B981" fill="url(#closedGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* T65 pipeline */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 lg:col-span-2">
        <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
          <Calendar size={16} className="text-amber-400" /> T65 Pipeline (Turning 65 Soon)
        </h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {t65Buckets.map(b => (
            <div key={b.label} className="bg-gray-800 border border-gray-700 rounded-xl p-4 text-center">
              <p className="text-3xl font-bold text-white">{b.count}</p>
              <p className="text-gray-400 text-xs mt-1">Within {b.label}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Summary stats */}
      <div className="bg-gray-900 border border-gray-800 rounded-xl p-5 lg:col-span-2">
        <h3 className="text-white font-semibold text-sm mb-4 flex items-center gap-2">
          <BarChart3 size={16} className="text-amber-400" /> Pipeline Summary
        </h3>
        <div className="grid grid-cols-3 lg:grid-cols-6 gap-3">
          {stageCounts.map(s => (
            <div key={s.key} className="text-center">
              <div className={`text-2xl font-bold ${s.color}`}>{s.count}</div>
              <div className="text-gray-500 text-xs mt-0.5">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ProspectSlideout
// ---------------------------------------------------------------------------
function ProspectSlideout({ prospect: initialProspect, onClose, onUpdate, onConvert }) {
  const [prospect, setProspect] = useState(initialProspect);
  const [activities, setActivities]     = useState([]);
  const [note, setNote]                 = useState('');
  const [saving, setSaving]             = useState(false);
  const [editMode, setEditMode]         = useState(false);
  const [editForm, setEditForm]         = useState({});
  const [movingStageTo, setMovingStageTo] = useState(null);
  const [loadingActivities, setLoadingActivities] = useState(false);

  useEffect(() => {
    setProspect(initialProspect);
    loadActivities(initialProspect.id);
  }, [initialProspect.id]);

  async function loadActivities(id) {
    setLoadingActivities(true);
    try {
      const r = await fetch(`${BASE}/api/prospecting/prospects/${id}/activities`, { credentials: 'include' });
      if (r.ok) {
        const j = await r.json();
        setActivities(j.activities || j || []);
      }
    } catch {} finally { setLoadingActivities(false); }
  }

  async function saveNote() {
    if (!note.trim()) return;
    setSaving(true);
    try {
      const r = await fetch(`${BASE}/api/prospecting/prospects/${prospect.id}/activities`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ activity_type: 'note', note: note.trim() }),
      });
      if (r.ok) {
        setNote('');
        loadActivities(prospect.id);
        onUpdate();
      }
    } finally { setSaving(false); }
  }

  async function moveToStage(stageKey) {
    setMovingStageTo(stageKey);
    try {
      await fetch(`${BASE}/api/prospecting/prospects/${prospect.id}/stage`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ stage: stageKey }),
      });
      setProspect(p => ({ ...p, stage: stageKey }));
      onUpdate();
    } finally { setMovingStageTo(null); }
  }

  async function toggleTcpa() {
    const newVal = !prospect.tcpa_consent;
    try {
      const r = await fetch(`${BASE}/api/prospecting/prospects/${prospect.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ tcpa_consent: newVal, tcpa_consent_at: newVal ? new Date().toISOString() : null }),
      });
      if (r.ok) {
        setProspect(p => ({ ...p, tcpa_consent: newVal, tcpa_consent_at: newVal ? new Date().toISOString() : null }));
        onUpdate();
      }
    } catch {}
  }

  async function saveEdit() {
    setSaving(true);
    try {
      const r = await fetch(`${BASE}/api/prospecting/prospects/${prospect.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(editForm),
      });
      if (r.ok) {
        const j = await r.json();
        setProspect(p => ({ ...p, ...editForm }));
        setEditMode(false);
        onUpdate();
      }
    } finally { setSaving(false); }
  }

  function startEdit() {
    setEditForm({
      first_name:     prospect.first_name || '',
      last_name:      prospect.last_name  || '',
      phone:          prospect.phone      || '',
      email:          prospect.email      || '',
      lead_source:    prospect.lead_source || '',
      insurance_type: prospect.insurance_type || 'medicare_advantage',
      priority:       prospect.priority   || 'normal',
      t65_date:       prospect.t65_date   ? prospect.t65_date.slice(0,10) : '',
      soa_status:     prospect.soa_status || 'not_required',
      notes:          prospect.notes      || '',
    });
    setEditMode(true);
  }

  const ACTIVITY_ICONS = {
    note:        <FileText size={12} className="text-gray-400" />,
    call:        <Phone size={12} className="text-blue-400" />,
    email:       <Mail size={12} className="text-purple-400" />,
    sms:         <MessageSquare size={12} className="text-amber-400" />,
    stage_change:<ArrowRight size={12} className="text-green-400" />,
  };

  const currentStage = STAGES.find(s => s.key === prospect.stage);

  return (
    <div className="fixed inset-0 z-40 flex justify-end">
      {/* Backdrop */}
      <div className="flex-1 bg-black/50" onClick={onClose} />

      {/* Panel */}
      <div className="w-full max-w-md bg-gray-900 border-l border-gray-800 flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 bg-amber-400/20 border border-amber-400/30 rounded-full flex items-center justify-center">
              <span className="text-amber-400 text-sm font-bold">
                {(prospect.first_name?.[0] || '') + (prospect.last_name?.[0] || '')}
              </span>
            </div>
            <div>
              <p className="text-white font-semibold">{prospect.first_name} {prospect.last_name}</p>
              {currentStage && (
                <span className={`text-xs ${currentStage.color}`}>{currentStage.label}</span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {!editMode && (
              <button onClick={startEdit} className="text-xs text-gray-400 hover:text-white border border-gray-700 hover:border-gray-500 px-2 py-1 rounded transition">
                Edit
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-white transition"><X size={20} /></button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* Contact info / edit form */}
          <div className="px-5 py-4 border-b border-gray-800">
            {editMode ? (
              <div className="flex flex-col gap-3">
                <div className="grid grid-cols-2 gap-3">
                  {[
                    { key: 'first_name', label: 'First Name' },
                    { key: 'last_name',  label: 'Last Name' },
                  ].map(f => (
                    <div key={f.key}>
                      <label className="text-gray-500 text-xs mb-1 block">{f.label}</label>
                      <input
                        value={editForm[f.key]}
                        onChange={e => setEditForm(ef => ({ ...ef, [f.key]: e.target.value }))}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-amber-400"
                      />
                    </div>
                  ))}
                </div>
                {[
                  { key: 'phone', label: 'Phone', type: 'tel' },
                  { key: 'email', label: 'Email', type: 'email' },
                  { key: 't65_date', label: 'T65 Date', type: 'date' },
                ].map(f => (
                  <div key={f.key}>
                    <label className="text-gray-500 text-xs mb-1 block">{f.label}</label>
                    <input
                      type={f.type || 'text'}
                      value={editForm[f.key]}
                      onChange={e => setEditForm(ef => ({ ...ef, [f.key]: e.target.value }))}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-amber-400"
                    />
                  </div>
                ))}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-gray-500 text-xs mb-1 block">Lead Source</label>
                    <select
                      value={editForm.lead_source}
                      onChange={e => setEditForm(ef => ({ ...ef, lead_source: e.target.value }))}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-amber-400"
                    >
                      {LEAD_SOURCES.map(s => <option key={s} value={s}>{sourceLabel(s)}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-gray-500 text-xs mb-1 block">Insurance Type</label>
                    <select
                      value={editForm.insurance_type}
                      onChange={e => setEditForm(ef => ({ ...ef, insurance_type: e.target.value }))}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-amber-400"
                    >
                      {Object.entries(INSURANCE_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-gray-500 text-xs mb-1 block">Priority</label>
                    <select
                      value={editForm.priority}
                      onChange={e => setEditForm(ef => ({ ...ef, priority: e.target.value }))}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-amber-400"
                    >
                      <option value="urgent">Urgent</option>
                      <option value="high">High</option>
                      <option value="normal">Normal</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-gray-500 text-xs mb-1 block">SOA Status</label>
                    <select
                      value={editForm.soa_status}
                      onChange={e => setEditForm(ef => ({ ...ef, soa_status: e.target.value }))}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-amber-400"
                    >
                      <option value="not_required">Not Required</option>
                      <option value="pending">Pending</option>
                      <option value="completed">Completed</option>
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-gray-500 text-xs mb-1 block">Notes</label>
                  <textarea
                    value={editForm.notes}
                    onChange={e => setEditForm(ef => ({ ...ef, notes: e.target.value }))}
                    rows={3}
                    className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 text-sm text-white focus:outline-none focus:border-amber-400 resize-none"
                  />
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={saveEdit}
                    disabled={saving}
                    className="flex-1 bg-amber-400 text-gray-900 font-semibold py-2 rounded-lg hover:bg-amber-300 transition text-sm disabled:opacity-50"
                  >
                    {saving ? 'Saving...' : 'Save Changes'}
                  </button>
                  <button
                    onClick={() => setEditMode(false)}
                    className="px-4 py-2 border border-gray-700 text-gray-400 hover:text-white rounded-lg transition text-sm"
                  >
                    Cancel
                  </button>
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <p className="text-gray-500 text-xs">Phone</p>
                  <p className="text-white">{fmtPhone(prospect.phone)}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Email</p>
                  <p className="text-white truncate">{prospect.email || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Lead Source</p>
                  <p className="text-white">{sourceLabel(prospect.lead_source)}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Insurance</p>
                  <p className="text-white">{INSURANCE_TYPES[prospect.insurance_type]?.label || '—'}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">T65 Date</p>
                  <p className="text-white">{fmtDate(prospect.t65_date)}</p>
                </div>
                <div>
                  <p className="text-gray-500 text-xs">Added</p>
                  <p className="text-white">{fmtDate(prospect.created_at)}</p>
                </div>
                {prospect.soa_status && (
                  <div>
                    <p className="text-gray-500 text-xs">SOA Status</p>
                    <p className="text-white capitalize">{prospect.soa_status.replace(/_/g,' ')}</p>
                  </div>
                )}
                {prospect.notes && (
                  <div className="col-span-2">
                    <p className="text-gray-500 text-xs">Notes</p>
                    <p className="text-gray-300 text-sm">{prospect.notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Stage mover */}
          {!editMode && (
            <div className="px-5 py-4 border-b border-gray-800">
              <p className="text-gray-500 text-xs mb-2">Move to Stage</p>
              <div className="flex flex-wrap gap-1.5">
                {STAGES.map(s => (
                  <button
                    key={s.key}
                    onClick={() => moveToStage(s.key)}
                    disabled={prospect.stage === s.key || movingStageTo === s.key}
                    className={`px-2.5 py-1 rounded-lg text-xs font-medium transition ${
                      prospect.stage === s.key
                        ? `${s.bg} text-white opacity-90 cursor-default`
                        : `border ${s.border} ${s.color} bg-transparent hover:bg-gray-800 disabled:opacity-40`
                    }`}
                  >
                    {movingStageTo === s.key ? '...' : s.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* TCPA toggle */}
          {!editMode && (
            <div className="px-5 py-4 border-b border-gray-800">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Shield size={14} className={prospect.tcpa_consent ? 'text-green-400' : 'text-gray-600'} />
                  <span className="text-white text-sm font-semibold">TCPA Consent</span>
                </div>
                <button
                  onClick={toggleTcpa}
                  className={`relative w-10 h-5 rounded-full transition ${prospect.tcpa_consent ? 'bg-green-500' : 'bg-gray-600'}`}
                >
                  <span className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${prospect.tcpa_consent ? 'translate-x-5' : 'translate-x-0'}`} />
                </button>
              </div>
              {prospect.tcpa_consent && prospect.tcpa_consent_at && (
                <p className="text-gray-500 text-xs">Obtained {fmtDate(prospect.tcpa_consent_at)}</p>
              )}
              {!prospect.tcpa_consent && (
                <p className="text-red-400 text-xs">No consent on file — do not send unsolicited marketing.</p>
              )}
              <p className="text-gray-600 text-xs mt-1.5 italic">
                By toggling consent, you confirm the prospect agreed to receive marketing communications.
              </p>
            </div>
          )}

          {/* Activity timeline */}
          {!editMode && (
            <div className="px-5 py-4 border-b border-gray-800">
              <p className="text-gray-400 text-xs font-semibold uppercase mb-3">Activity</p>
              {loadingActivities && <p className="text-gray-600 text-xs">Loading...</p>}
              {!loadingActivities && activities.length === 0 && (
                <p className="text-gray-600 text-xs">No activities yet.</p>
              )}
              <div className="flex flex-col gap-3">
                {activities.map((a, i) => (
                  <div key={a.id || i} className="flex items-start gap-2.5">
                    <div className="w-5 h-5 bg-gray-800 border border-gray-700 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                      {ACTIVITY_ICONS[a.activity_type] || <Activity size={12} className="text-gray-500" />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-gray-300 text-xs">{a.note || a.description || a.activity_type}</p>
                      <p className="text-gray-600 text-xs mt-0.5">{timeAgo(a.created_at)}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Add note */}
          {!editMode && (
            <div className="px-5 py-4">
              <p className="text-gray-400 text-xs font-semibold uppercase mb-2">Add Note</p>
              <textarea
                value={note}
                onChange={e => setNote(e.target.value)}
                placeholder="Log a call, note, or follow-up..."
                rows={3}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 resize-none"
              />
              <button
                onClick={saveNote}
                disabled={saving || !note.trim()}
                className="mt-2 bg-amber-400 text-gray-900 font-semibold px-4 py-1.5 rounded-lg hover:bg-amber-300 transition text-xs disabled:opacity-40 flex items-center gap-1.5"
              >
                {saving ? <><RefreshCw size={12} className="animate-spin" /> Saving...</> : <><Check size={12} /> Save Note</>}
              </button>
            </div>
          )}
        </div>

        {/* Footer: Convert to Client */}
        {prospect.stage === 'closed_won' && (
          <div className="px-5 py-4 border-t border-gray-800">
            <button
              onClick={() => onConvert(prospect)}
              className="w-full bg-emerald-500 hover:bg-emerald-400 text-white font-semibold py-2.5 rounded-xl transition text-sm flex items-center justify-center gap-2"
            >
              <CheckCircle2 size={16} /> Convert to Client
            </button>
            <p className="text-gray-600 text-xs text-center mt-1.5">
              Creates a client record and adds to Enrollment Tracker.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// AddProspectModal
// ---------------------------------------------------------------------------
function AddProspectModal({ stage: initialStage, onClose, onSave }) {
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');
  const [form, setForm]     = useState({
    first_name:     '',
    last_name:      '',
    phone:          '',
    email:          '',
    lead_source:    'referral',
    insurance_type: 'medicare_advantage',
    priority:       'normal',
    notes:          '',
    t65_date:       '',
    stage:          initialStage || 'lead',
  });

  function setField(k, v) { setForm(f => ({ ...f, [k]: v })); }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!form.first_name.trim() || !form.last_name.trim()) {
      setError('First and last name are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      const r = await fetch(`${BASE}/api/prospecting/prospects`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify(form),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setError(j.error || 'Failed to add prospect.');
        return;
      }
      onSave();
    } finally { setSaving(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-md max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-800">
          <h3 className="text-white font-semibold">Add Prospect</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-white transition"><X size={20} /></button>
        </div>

        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-6 py-4 flex flex-col gap-4">
          {error && (
            <div className="flex items-center gap-2 bg-red-900/30 border border-red-500/30 rounded-lg px-3 py-2 text-red-400 text-sm">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs mb-1 block">First Name *</label>
              <input
                value={form.first_name}
                onChange={e => setField('first_name', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-400"
                required
              />
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Last Name *</label>
              <input
                value={form.last_name}
                onChange={e => setField('last_name', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-400"
                required
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Phone</label>
              <input
                type="tel"
                value={form.phone}
                onChange={e => setField('phone', e.target.value)}
                placeholder="(555) 000-0000"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-400"
              />
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Email</label>
              <input
                type="email"
                value={form.email}
                onChange={e => setField('email', e.target.value)}
                placeholder="jane@example.com"
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-400"
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Lead Source</label>
              <select
                value={form.lead_source}
                onChange={e => setField('lead_source', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-400"
              >
                {LEAD_SOURCES.map(s => <option key={s} value={s}>{sourceLabel(s)}</option>)}
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Insurance Type</label>
              <select
                value={form.insurance_type}
                onChange={e => setField('insurance_type', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-400"
              >
                {Object.entries(INSURANCE_TYPES).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Priority</label>
              <select
                value={form.priority}
                onChange={e => setField('priority', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-400"
              >
                <option value="urgent">Urgent</option>
                <option value="high">High</option>
                <option value="normal">Normal</option>
                <option value="low">Low</option>
              </select>
            </div>
            <div>
              <label className="text-gray-400 text-xs mb-1 block">Initial Stage</label>
              <select
                value={form.stage}
                onChange={e => setField('stage', e.target.value)}
                className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-400"
              >
                {STAGES.slice(0,4).map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-gray-400 text-xs mb-1 block">T65 Date (65th Birthday)</label>
            <input
              type="date"
              value={form.t65_date}
              onChange={e => setField('t65_date', e.target.value)}
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-amber-400"
            />
          </div>

          <div>
            <label className="text-gray-400 text-xs mb-1 block">Notes</label>
            <textarea
              value={form.notes}
              onChange={e => setField('notes', e.target.value)}
              rows={3}
              placeholder="Initial notes about this prospect..."
              className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-amber-400 resize-none"
            />
          </div>
        </form>

        {/* Footer */}
        <div className="flex items-center justify-between px-6 py-4 border-t border-gray-800">
          <button onClick={onClose} className="text-gray-400 hover:text-white text-sm transition">Cancel</button>
          <button
            onClick={handleSubmit}
            disabled={saving || !form.first_name.trim() || !form.last_name.trim()}
            className="bg-amber-400 text-gray-900 font-semibold px-6 py-2 rounded-lg hover:bg-amber-300 transition text-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center gap-2"
          >
            {saving ? <><RefreshCw size={14} className="animate-spin" /> Adding...</> : <><Plus size={14} /> Add Prospect</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// ConvertModal
// ---------------------------------------------------------------------------
function ConvertModal({ prospect, onClose, onConverted }) {
  const [converting, setConverting] = useState(false);
  const [error, setError]           = useState('');

  async function handleConvert() {
    setConverting(true);
    setError('');
    try {
      const r = await fetch(`${BASE}/api/prospecting/prospects/${prospect.id}/convert`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({}),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        setError(j.error || 'Conversion failed. Please try again.');
        return;
      }
      onConverted();
    } finally { setConverting(false); }
  }

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl w-full max-w-sm">
        <div className="px-6 py-5">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-emerald-500/20 border border-emerald-500/30 rounded-full flex items-center justify-center">
              <CheckCircle2 size={20} className="text-emerald-400" />
            </div>
            <h3 className="text-white font-semibold">Convert to Client</h3>
          </div>

          <p className="text-gray-300 text-sm mb-2">
            Convert <span className="text-white font-semibold">{prospect?.first_name} {prospect?.last_name}</span> to a client?
          </p>
          <p className="text-gray-500 text-xs mb-4">
            This will create a client record and add them to the Enrollment Tracker.
          </p>

          {error && (
            <div className="flex items-center gap-2 bg-red-900/30 border border-red-500/30 rounded-lg px-3 py-2 text-red-400 text-sm mb-4">
              <AlertCircle size={14} /> {error}
            </div>
          )}

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="flex-1 py-2.5 border border-gray-700 text-gray-400 hover:text-white rounded-xl transition text-sm"
            >
              Cancel
            </button>
            <button
              onClick={handleConvert}
              disabled={converting}
              className="flex-1 py-2.5 bg-emerald-500 hover:bg-emerald-400 text-white font-semibold rounded-xl transition text-sm disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {converting
                ? <><RefreshCw size={14} className="animate-spin" /> Converting...</>
                : <><CheckCircle2 size={14} /> Convert</>
              }
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function ProspectingPipeline() {
  const navigate = useNavigate();
  const [tab, setTab]                       = useState('pipeline');
  const [pipeline, setPipeline]             = useState({});
  const [prospects, setProspects]           = useState([]);
  const [campaigns, setCampaigns]           = useState([]);
  const [analytics, setAnalytics]           = useState(null);
  const [loading, setLoading]               = useState(true);
  const [allowed, setAllowed]               = useState(true);
  const [allowedReason, setAllowedReason]   = useState('');
  const [selectedProspect, setSelectedProspect] = useState(null);
  const [showAddModal, setShowAddModal]     = useState(null); // stage key or null
  const [showConvertModal, setShowConvertModal] = useState(null); // prospect or null
  const [creditBalance, setCreditBalance]   = useState(null);
  const [toast, setToast]                   = useState(null);

  // ---------------------------------------------------------------------------
  // API helpers
  // ---------------------------------------------------------------------------
  async function checkPermission() {
    try {
      const r = await fetch(`${BASE}/api/prospecting/permission-check`, { credentials: 'include' });
      const j = await r.json();
      setAllowed(j.allowed !== false);
      setAllowedReason(j.reason || '');
    } catch {
      // If endpoint doesn't exist, assume allowed
      setAllowed(true);
    }
  }

  async function loadPipeline() {
    setLoading(true);
    try {
      const r = await fetch(`${BASE}/api/prospecting/pipeline`, { credentials: 'include' });
      if (r.ok) {
        const j = await r.json();
        const pipelineData = j.pipeline || j;
        setPipeline(pipelineData);
        // Flatten for prospects table
        setProspects(Object.values(pipelineData).flat());
      }
    } catch {} finally { setLoading(false); }
  }

  async function loadCampaigns() {
    try {
      const r = await fetch(`${BASE}/api/prospecting/campaigns`, { credentials: 'include' });
      if (r.ok) {
        const j = await r.json();
        setCampaigns(j.campaigns || j.data || []);
      }
    } catch {}
  }

  async function loadCreditBalance() {
    try {
      const r = await fetch(`${BASE}/api/prospecting/credits/balance`, { credentials: 'include' });
      if (r.ok) {
        const j = await r.json();
        setCreditBalance(j.balance ?? 0);
      }
    } catch {}
  }

  async function loadAnalytics() {
    try {
      const r = await fetch(`${BASE}/api/prospecting/analytics`, { credentials: 'include' });
      if (r.ok) {
        const j = await r.json();
        setAnalytics(j);
      }
    } catch {}
  }

  async function moveStage(prospectId, newStage, note = '') {
    try {
      await fetch(`${BASE}/api/prospecting/prospects/${prospectId}/stage`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ stage: newStage, note }),
      });
      loadPipeline();
    } catch {}
  }

  // ---------------------------------------------------------------------------
  // Effects
  // ---------------------------------------------------------------------------
  useEffect(() => {
    checkPermission();
    loadPipeline();
    loadCampaigns();
    loadAnalytics();
    loadCreditBalance();

    // Handle ?credits_purchased=1 redirect from Stripe
    const params = new URLSearchParams(window.location.search);
    if (params.get('credits_purchased') === '1') {
      setToast('Credits added to your account!');
      setTimeout(() => setToast(null), 4000);
      // Clean URL without reload
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []);

  // Reload campaigns when switching to campaigns tab
  useEffect(() => {
    if (tab === 'campaigns') { loadCampaigns(); loadCreditBalance(); }
    if (tab === 'analytics') loadAnalytics();
  }, [tab]);

  // ---------------------------------------------------------------------------
  // Not allowed state
  // ---------------------------------------------------------------------------
  if (!allowed) {
    return (
      <div className="min-h-screen bg-gray-950 flex items-center justify-center">
        <div className="text-center">
          <Target size={48} className="text-gray-600 mx-auto mb-4" />
          <h2 className="text-white text-xl font-bold mb-2">Prospecting Not Enabled</h2>
          <p className="text-gray-400">Your agency has not enabled the Prospecting Pipeline.</p>
          <p className="text-gray-500 text-sm mt-2">Contact your agency owner to enable this feature.</p>
          {allowedReason && <p className="text-gray-600 text-xs mt-2">{allowedReason}</p>}
        </div>
      </div>
    );
  }

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const TABS = [
    { key: 'pipeline',  label: 'Pipeline'     },
    { key: 'prospects', label: 'Prospects'    },
    { key: 'campaigns', label: 'Campaigns'    },
    { key: 'sources',   label: 'Lead Sources' },
    { key: 'analytics', label: 'Analytics'   },
  ];

  return (
    <div className="min-h-screen bg-gray-950 text-white">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Target className="text-amber-400" size={24} />
            <h1 className="text-xl font-bold text-white">Prospecting Pipeline</h1>
            {loading && <RefreshCw size={14} className="text-gray-600 animate-spin" />}
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate('/prospecting/import')}
              className="bg-gray-800 border border-gray-700 text-gray-300 hover:text-white hover:border-gray-500 font-medium px-4 py-2 rounded-lg transition text-sm flex items-center gap-2"
            >
              <Upload size={15} /> Bulk Import
            </button>
            <button
              onClick={() => setShowAddModal('lead')}
              className="bg-amber-400 text-gray-900 font-semibold px-4 py-2 rounded-lg hover:bg-amber-300 transition text-sm flex items-center gap-2"
            >
              <Plus size={16} /> Add Prospect
            </button>
          </div>
        </div>

        {/* Tab bar */}
        <div className="flex gap-1 mt-4">
          {TABS.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition ${
                tab === t.key
                  ? 'bg-amber-400 text-gray-900'
                  : 'text-gray-400 hover:text-white hover:bg-gray-800'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="p-6 overflow-x-auto">
        {tab === 'pipeline' && (
          <PipelineTab
            pipeline={pipeline}
            loading={loading}
            onProspectClick={setSelectedProspect}
            onAddProspect={setShowAddModal}
            moveStage={moveStage}
            setShowConvertModal={setShowConvertModal}
          />
        )}
        {tab === 'prospects' && (
          <ProspectsTab
            prospects={prospects}
            onProspectClick={setSelectedProspect}
            onAddProspect={setShowAddModal}
            onReload={loadPipeline}
          />
        )}
        {tab === 'campaigns' && (
          <CampaignsTab
            campaigns={campaigns}
            onReload={loadCampaigns}
            creditBalance={creditBalance}
            onCreditsChanged={loadCreditBalance}
          />
        )}
        {tab === 'sources' && (
          <LeadSourcesTab
            prospects={prospects}
          />
        )}
        {tab === 'analytics' && (
          <AnalyticsTab
            analytics={analytics}
            prospects={prospects}
          />
        )}
      </div>

      {/* Prospect slideout */}
      {selectedProspect && (
        <ProspectSlideout
          prospect={selectedProspect}
          onClose={() => setSelectedProspect(null)}
          onUpdate={loadPipeline}
          onConvert={(p) => { setSelectedProspect(null); setShowConvertModal(p); }}
        />
      )}

      {/* Modals */}
      {showAddModal && (
        <AddProspectModal
          stage={showAddModal}
          onClose={() => setShowAddModal(null)}
          onSave={() => { loadPipeline(); setShowAddModal(null); }}
        />
      )}
      {showConvertModal && (
        <ConvertModal
          prospect={showConvertModal}
          onClose={() => setShowConvertModal(null)}
          onConverted={() => { loadPipeline(); setShowConvertModal(null); }}
        />
      )}

      {/* Toast notification */}
      {toast && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-emerald-600 text-white px-5 py-3 rounded-xl shadow-xl text-sm font-medium z-50 flex items-center gap-2">
          <Check size={16} /> {toast}
        </div>
      )}
    </div>
  );
}
